import { useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  exchangeErpSession, fetchSsoOptions, loginWithPassword, readSsoReturn, safeRedirect, ssoStartUrl, stripSsoReturn,
  type AuthOutcome, type ErpSessionRequest, type SsoOptions,
} from './api.ts'
import type { LoginMessages } from './locales.ts'
import { SINOCST_LOGO_URL } from './sinocst-logo-url.ts'
import styles from './LoginPage.module.css'

/** localStorage key holding the remembered account name; the password is never stored. */
export const REMEMBERED_USERNAME_KEY = 'sinocst-login.username'

/** Navigation the page performs: `replace` after sign-in, `assign` to start SSO. `window.location` satisfies it. */
export type LoginNavigation = Pick<Location, 'replace' | 'assign'>

/** Props of {@link LoginPage}. */
export interface LoginPageProps {
  /** Dictionary for every visible string. */
  readonly messages: LoginMessages
  /** Receives the post-sign-in and SSO-start navigations. */
  readonly navigation: LoginNavigation
}

type Notice =
  | { readonly tone: 'error'; readonly text: string }
  | { readonly tone: 'info'; readonly text: string }

const SSO_DISABLED: SsoOptions = { enabled: false, label: '', startUrl: '' }
/** Native aspect ratio of {@link SINOCST_LOGO_URL} (410 × 406). */
const LOGO_ASPECT = 406 / 410
const LOGO_SIZE = 28
/** Console WebGL globe page, served from the login dist `public/` copy. */
const SCENE_SRC = `${import.meta.env.BASE_URL}SC-AI-ERP-login-bg-v5.html?scene=1`

function readRememberedUsername(): string | null {
  try {
    return window.localStorage.getItem(REMEMBERED_USERNAME_KEY)
  } catch {
    // SecurityError: storage disabled by browser policy; nothing was remembered.
    return null
  }
}

function writeRememberedUsername(username: string | null): void {
  try {
    if (username === null) window.localStorage.removeItem(REMEMBERED_USERNAME_KEY)
    else window.localStorage.setItem(REMEMBERED_USERNAME_KEY, username)
  } catch {
    // SecurityError or QuotaExceededError: remembering is a convenience and must not block navigation.
  }
}

function erpSessionRequest(search: string, redirect: string): ErpSessionRequest | undefined {
  const sso = readSsoReturn(search)
  if (sso === undefined) return undefined
  return {
    erp_token: sso.erpToken,
    ...sso.userId === '' ? {} : { user_id: sso.userId },
    ...sso.erpTenantId === '' ? {} : { erp_tenant_id: sso.erpTenantId },
    ...sso.mandt === '' ? {} : { mandt: sso.mandt },
    redirect,
  }
}

/**
 * The enterprise sign-in page. On mount it consumes an SSO return (`erp_token` or `access_token` in the query):
 * the identity keys leave the address bar before the token is exchanged.
 * @param props - see {@link LoginPageProps}.
 * @returns the page element.
 */
export function LoginPage({ messages, navigation }: LoginPageProps) {
  const redirect = useMemo(() => safeRedirect(new URLSearchParams(window.location.search).get('redirect')), [])
  const [remembered] = useState(readRememberedUsername)
  const [username, setUsername] = useState(remembered ?? '')
  const [password, setPassword] = useState('')
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [remember, setRemember] = useState(remembered !== null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<Notice | undefined>(undefined)
  const [sso, setSso] = useState<SsoOptions>(SSO_DISABLED)

  const settle = (outcome: AuthOutcome, onSignedIn?: () => void): void => {
    switch (outcome.kind) {
      case 'signed-in':
        onSignedIn?.()
        navigation.replace(outcome.redirect)
        return
      case 'rejected':
        setNotice({ tone: 'error', text: outcome.message ?? messages.loginFailed })
        break
      case 'unavailable':
        setNotice({ tone: 'error', text: messages.backendUnavailable })
        break
      default:
        assertNever(outcome)
    }
    setBusy(false)
  }

  useEffect(() => {
    let active = true
    const request = erpSessionRequest(window.location.search, redirect)
    if (request !== undefined) {
      stripSsoReturn()
      setBusy(true)
      setNotice({ tone: 'info', text: messages.ssoExchanging })
      void exchangeErpSession(request).then((outcome) => {
        if (active) settle(outcome)
      })
    }
    void fetchSsoOptions().then((options) => {
      if (active) setSso(options)
    })
    return () => {
      active = false
    }
    // Mount-only: the SSO return is consumed once per page load.
  }, [])

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    if (busy) return
    const account = username.trim()
    if (account === '' || password === '') {
      setNotice({ tone: 'error', text: messages.missingCredentials })
      return
    }
    setBusy(true)
    setNotice(undefined)
    void loginWithPassword({ username: account, password, redirect }).then((outcome) => {
      settle(outcome, () => { writeRememberedUsername(remember ? account : null) })
    })
  }

  const startSso = (): void => {
    navigation.assign(ssoStartUrl(sso.startUrl, window.location.origin, redirect))
  }

  return (
    <div className={styles.page}>
      <iframe
        className={styles.scene}
        src={SCENE_SRC}
        title=""
        tabIndex={-1}
        aria-hidden="true"
      />

      <header className={styles.top}>
        <div className={styles.brand}>
          <img src={SINOCST_LOGO_URL} width={LOGO_SIZE} height={LOGO_SIZE * LOGO_ASPECT} alt="" aria-hidden="true" />
          <span>{messages.brand}</span>
        </div>
        <div className={styles.status}><i />{messages.networkStatus}</div>
      </header>

      <main className={styles.ui}>
        <section className={styles.hero}>
          <h1 className={styles.heroTitle}>{messages.heroTitle}</h1>
          <p className={styles.lead}>{messages.heroLead}</p>
          <div className={styles.kpis}>
            <div><span className={styles.kpiValue}>128</span><small>{messages.kpiSignals}</small></div>
            <div><span className={styles.kpiValue}>24</span><small>{messages.kpiCapabilities}</small></div>
            <div><span className={styles.kpiValue}>03</span><small>{messages.kpiPendingTasks}</small></div>
          </div>
        </section>

        <section className={styles.card}>
          <h2 className={styles.cardTitle}>{messages.cardTitle}</h2>
          <p className={styles.sub}>{messages.cardSubtitle}</p>
          <form onSubmit={submit} noValidate>
            <label className={styles.fieldLabel} htmlFor="sinocst-login-account">{messages.username}</label>
            <div className={styles.field}>
              <svg className={styles.leadIcon} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="10" cy="7" r="3.2" /><path d="M3.8 16.5c.8-3 3.2-4.6 6.2-4.6s5.4 1.6 6.2 4.6" /></svg>
              <input
                id="sinocst-login-account"
                type="text"
                value={username}
                onChange={(event) => { setUsername(event.target.value) }}
                placeholder={messages.username}
                autoComplete="username"
              />
            </div>
            <label className={styles.fieldLabel} htmlFor="sinocst-login-password">{messages.password}</label>
            <div className={styles.field}>
              <svg className={styles.leadIcon} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="4" y="9" width="12" height="8" rx="2" /><path d="M7 9V6.5a3 3 0 0 1 6 0V9" /></svg>
              <input
                id="sinocst-login-password"
                type={passwordVisible ? 'text' : 'password'}
                value={password}
                onChange={(event) => { setPassword(event.target.value) }}
                placeholder={messages.password}
                autoComplete="current-password"
              />
              <button
                className={styles.eye}
                type="button"
                aria-label={passwordVisible ? messages.hidePassword : messages.showPassword}
                aria-pressed={passwordVisible}
                onClick={() => { setPasswordVisible(visible => !visible) }}
              >
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1.8 10S5 4.5 10 4.5 18.2 10 18.2 10 15 15.5 10 15.5 1.8 10 1.8 10Z" /><circle cx="10" cy="10" r="2.4" /></svg>
              </button>
            </div>
            <div className={styles.row}>
              <label className={styles.check}>
                <input type="checkbox" checked={remember} onChange={(event) => { setRemember(event.target.checked) }} />
                {messages.remember}
              </label>
              <button className={styles.link} type="button" onClick={() => { setNotice({ tone: 'info', text: messages.forgotPasswordHint }) }}>
                {messages.forgotPassword}
              </button>
            </div>
            {notice?.tone === 'error' && <p className={styles.error} role="alert">{notice.text}</p>}
            {notice?.tone === 'info' && <p className={styles.info} role="status">{notice.text}</p>}
            <button className={`${styles.login} ${busy ? styles.loading : ''}`} type="submit" disabled={busy} aria-busy={busy}>
              <span className={styles.loginText}>{busy ? messages.submitting : messages.submit}</span>
              <span className={styles.spin} aria-hidden="true" />
            </button>
          </form>
          {sso.enabled && (
            <>
              <div className={styles.or}>{messages.or}</div>
              <button className={styles.sso} type="button" onClick={startSso} disabled={busy}>
                {sso.label === '' ? messages.ssoDefaultLabel : sso.label}
              </button>
            </>
          )}
          <p className={styles.note}>{messages.note}</p>
        </section>
      </main>

      <div className={styles.foot}>{messages.footer}</div>
    </div>
  )
}

function assertNever(value: never): never {
  throw new Error(`unhandled auth outcome ${JSON.stringify(value)}`)
}
